import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';
import { Shield, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PasswordInput from '../components/PasswordInput';
import { setToken } from '../lib/authToken';
import { passwordPolicyError } from '../lib/password';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('client');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  // El error se renderiza arriba del form (línea ~118) pero el botón de
  // submit está al final — en mobile, tras llenar todo el form el error
  // de contraseñas quedaba fuera del viewport y el usuario no lo veía.
  const errorRef = useRef(null);
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);
  const [loading, setLoading] = useState(false);

  const [googleCredential, setGoogleCredential] = useState(null);
  const [googleRoleModal, setGoogleRoleModal] = useState(false);
  const [googleRole, setGoogleRole] = useState('client');
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (password !== confirmPassword) {
      setError(t('auth.errorPasswordsMatch'));
      return;
    }
    const pwdError = passwordPolicyError(password);
    if (pwdError) {
      setError(t(pwdError));
      return;
    }
    setLoading(true);
    try {
      const data = await register(email, password, name, role);
      if (!data.user) {
        // El backend responde igual exista o no la cuenta (anti-enumeración);
        // los pasos a seguir llegan por correo.
        setInfo(t('auth.checkEmailToContinue'));
        return;
      }
      if (data.user.role === 'provider') navigate('/provider/dashboard');
      else navigate('/client/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.errorRegister'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    setGoogleCredential(credentialResponse.credential);
    setGoogleRoleModal(true);
  };

  const handleGoogleRegister = async () => {
    if (!googleCredential) return;
    setGoogleLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/google`, {
        token: googleCredential,
        role: googleRole
      }, { withCredentials: true });
      if (res.status === 200) {
        if (res.data.token) setToken(res.data.token);
        if (res.data.user.role === 'provider') navigate('/provider/dashboard');
        else navigate('/client/dashboard');
        window.location.reload();
      }
    } catch (err) {
      setError(t('auth.googleRegisterError'));
      setGoogleRoleModal(false);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="grid lg:grid-cols-2 min-h-screen">
        <div className="flex items-center justify-center p-8 lg:p-12 relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-6 left-6 flex items-center gap-1.5 text-gray-500 hover:text-[#134168] transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('auth.goBack', 'Volver')}
          </button>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md"
          >
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img src={`${process.env.PUBLIC_URL}/images/logo.png`} alt="RedMobility" className="h-20 object-contain" onError={(e) => {
                  e.target.style.display='none';
                }} />
              </div>
              <h1 className="text-3xl font-bold text-[#111827]">{t('auth.registerTitle')}</h1>
              <p className="text-gray-500 mt-1">{t('auth.registerSubtitle')}</p>
            </div>

            {error && (
              <div ref={errorRef} className="mb-4 flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl p-3 text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {info && (
              <div className="mb-4 flex items-center gap-2 text-[#134168] bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm">
                <CheckCircle className="w-4 h-4 shrink-0" />
                {info}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.fullName')}</label>
                <input
                  type="text" value={name}
                  onChange={e => setName(e.target.value)}
                  required placeholder={t('auth.namePlaceholder')}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')}</label>
                <input
                  type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  required placeholder={t('auth.emailPlaceholder2')}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.password')}</label>
                <PasswordInput
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required placeholder={t('auth.passwordPlaceholder')}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPassword')}</label>
                <PasswordInput
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required placeholder={t('auth.confirmPasswordPlaceholder')}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth.accountType')}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setRole('client')}
                    className={`py-3 px-4 rounded-xl border-2 font-medium text-sm transition-all ${
                      role === 'client' ? 'border-[#134168] bg-blue-50 text-[#134168]' : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    🙋 {t('auth.accountClient')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole('provider')}
                    className={`py-3 px-4 rounded-xl border-2 font-medium text-sm transition-all ${
                      role === 'provider' ? 'border-[#134168] bg-blue-50 text-[#134168]' : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    🏢 {t('auth.accountProvider')}
                  </button>
                </div>
              </div>

              {role === 'client' && (
                <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700">
                  <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span><Trans i18nKey="auth.clientBenefit" /></span>
                </div>
              )}

              {role === 'provider' && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span><Trans i18nKey="auth.providerBenefit" /></span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#134168] text-white py-3 px-4 rounded-full font-bold hover:bg-[#1a5490] transition-all disabled:opacity-50"
              >
                {loading ? t('auth.registerInProgress') : t('auth.registerBtn')}
              </button>
            </form>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">{t('auth.registerWith')}</span></div>
            </div>

            <div className="w-full flex justify-center mb-6">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError(t('auth.googleErrorShort'))}
                size="large" shape="pill" text="signup_with"
              />
            </div>

            <div className="text-center">
              <span className="text-gray-500 text-sm">{t('auth.haveAccount')} </span>
              <button
                onClick={() => navigate('/login')}
                className="text-[#134168] text-sm font-medium hover:underline"
              >
                {t('auth.loginNow')}
              </button>
            </div>
          </motion.div>
        </div>

        <div className="hidden lg:block relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=85"
            alt="RedMobility"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
            <h2 className="text-4xl font-bold mb-4">{t('auth.marketplaceTaglineShort')}</h2>
            <div className="flex items-center gap-3 mt-6">
              <Shield className="w-6 h-6 text-yellow-400" />
              <span className="text-gray-200">{t('auth.trustBullets')}</span>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {googleRoleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4"
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="bg-white rounded-3xl p-8 w-full max-w-md"
            >
              <h2 className="text-2xl font-bold text-[#111827] mb-2">{t('auth.googleModalTitle')}</h2>
              <p className="text-gray-500 mb-6 text-sm">{t('auth.googleModalSubtitle')}</p>

              <div className="space-y-3 mb-6">
                {[
                  { val: 'client', label: t('auth.accountClient'), desc: t('auth.roleClient'), icon: '🙋' },
                  { val: 'provider', label: t('auth.accountProvider'), desc: t('auth.roleProvider'), icon: '🏢' }
                ].map(opt => (
                  <button
                    key={opt.val}
                    onClick={() => setGoogleRole(opt.val)}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${
                      googleRole === opt.val ? 'border-[#134168] bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <span className="text-3xl">{opt.icon}</span>
                    <div>
                      <div className="font-bold text-[#111827]">{opt.label}</div>
                      <div className="text-sm text-gray-500">{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>

              {googleRole === 'provider' && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700 mb-4">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{t('auth.providerApprovalNote')}</span>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setGoogleRoleModal(false)}
                  className="flex-1 py-3 border border-gray-200 rounded-full font-medium text-gray-600 hover:bg-gray-50 transition"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleGoogleRegister}
                  disabled={googleLoading}
                  className="flex-1 py-3 bg-[#134168] text-white rounded-full font-bold hover:bg-[#1a5490] transition disabled:opacity-50"
                >
                  {googleLoading ? t('auth.registering') : t('common.continue')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
